/**
 * Pure object-fit:cover projection for the Map Atlas system (BP-MAP-ATLAS-02).
 *
 * Given an atlas's native image dimensions, the container the image is painted
 * into (object-fit: cover), and a normalised source point (srcX, srcY ∈ [0,1]
 * of the native image), returns the pixel coordinate of that point inside the
 * container's coordinate space (origin = container top-left).
 *
 * This is the whole fix for the production drift bug: as long as the node
 * overlay and the displayed image share ONE container rect, a source-image
 * fraction always lands on the correct spot of the painted art — at any size,
 * any aspect ratio, any breakpoint. No hardcoded 16:9, no per-breakpoint
 * canvas table.
 *
 * Pure: no React, no DOM, no side effects.
 *
 * @param {object} p
 * @param {number} p.nativeW     native image width  (px)
 * @param {number} p.nativeH     native image height (px)
 * @param {number} p.containerW  container width  (px)
 * @param {number} p.containerH  container height (px)
 * @param {number} p.srcX        source x fraction 0–1
 * @param {number} p.srcY        source y fraction 0–1
 * @param {'center'|'bottomCenter'} [p.anchor]  semantic anchor of the consumer.
 *        Both anchors return the node's projected CENTER point; the consumer
 *        (e.g. CharacterFigurine) applies its own CSS transform so its feet land
 *        on that point. The parameter documents intent and is reserved for
 *        future per-anchor math.
 * @returns {{ x: number, y: number }} pixel position in the container, or
 *          { x: 0, y: 0 } if any dimension is non-positive.
 */
export function projectNode({ nativeW, nativeH, containerW, containerH, srcX, srcY, anchor = 'center' }) {
  void anchor; // reserved; both anchors project to the node centre (see docs above)

  if (
    !(containerW > 0) || !(containerH > 0) ||
    !(nativeW > 0) || !(nativeH > 0)
  ) {
    return { x: 0, y: 0 };
  }

  // object-fit: cover — the larger scale factor wins so the image fully covers
  // the container, overflowing on the other axis.
  const scale = Math.max(containerW / nativeW, containerH / nativeH);
  const renderedW = nativeW * scale;
  const renderedH = nativeH * scale;

  // Cover centres the image; overflow is split evenly (negative offset).
  const offsetX = (containerW - renderedW) / 2;
  const offsetY = (containerH - renderedH) / 2;

  return {
    x: srcX * renderedW + offsetX,
    y: srcY * renderedH + offsetY,
  };
}
